// widget/src/index.tsx
import { createRoot } from "react-dom/client"
import { ChatWidget } from "./ChatWidget"
import { FinChatWidget } from "./FinChatWidget"
import type { PageContext, WidgetMountOptions } from "./types"

const SESSION_TOKEN_KEY = "dyc-pearl-session"
const ROOT_ID = "dyc-pearl-root"

interface Settings {
  accent?: string
  dock_label?: string
}

function readScriptAttrs(): {
  customerName?: string
  customerEmail?: string
  customerId?: string
  apiBaseUrl?: string
  dockLabelOverride?: string
  variant?: string
} {
  if (typeof document === "undefined") return {}
  const script = document.querySelector<HTMLScriptElement>("script[data-pearl]")
  if (!script) return {}
  // UI variant selection. `data-variant="fin"` (or `?pearl_variant=fin` in
  // the URL) mounts the alternate "Pearl — Messenger" UI; anything else
  // mounts the default dark-glass ChatWidget. Lets us A/B the two skins
  // against the same backend.
  const urlVariant =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("pearl_variant") ??
        undefined
      : undefined
  return {
    customerName: script.dataset.customerName,
    customerEmail: script.dataset.customerEmail,
    customerId: script.dataset.customerId,
    apiBaseUrl: script.dataset.apiBase ?? new URL(script.src).origin,
    // data-dock-label on the script tag wins over the live config —
    // useful for the dashboard preview iframe (which can't easily mutate
    // the live config endpoint while typing).
    dockLabelOverride: script.dataset.dockLabel,
    variant: script.dataset.variant ?? urlVariant,
  }
}

/** Mint or reuse a session token via `GET /api/chat/session`. The
 *  backend's `validateSession` rejects arbitrary IDs, so we MUST get a
 *  real one before sending any messages. We cache the resolved token in
 *  localStorage so returning visitors keep the same session — but we
 *  ALWAYS validate the cached value against the backend on page load
 *  before trusting it. Tokens can be invalidated server-side (database
 *  wipe in staging, session-cleanup cron in prod) and the only signal
 *  the client otherwise gets is a 401 on the first message. Validating
 *  up front avoids the "first message vanishes silently" failure. */
interface SessionHandshake {
  token: string
  customerName: string | null
  customerEmail: string | null
  // Server-driven canvas kill-switch. Sourced from messaging_mode_config.
  // canvas_enabled — operator can flip in /chat/settings without a redeploy.
  // null when the field isn't on the response yet (older API / first-mount
  // failure); we default to TRUE in that case so deployment race conditions
  // don't silently regress the new UX.
  canvasEnabled: boolean | null
  // Mobile (≤767px) canvas flag — independent of desktop. null when absent
  // (older API / first-mount failure); resolves to FALSE in that case since
  // mobile canvas is opt-in.
  canvasEnabledMobile: boolean | null
  // Post-resolution CSAT prompt flag. Operator opt-in from /chat/settings,
  // sourced from messaging_mode_config.csat_enabled. Defaults to FALSE here
  // when the field is absent (older API / first-mount failure) — when off the
  // widget renders nothing extra, so erring toward "off" keeps the default
  // experience unchanged.
  csatEnabled: boolean
  // Latest conversation on this session, used to auto-resume after refresh
  // or cross-tab open. Filled only when the server is resuming a known
  // session token AND that session has a most-recent conversation row.
  // Null on fresh mint, empty thread, or older API responses.
  resumeConversation: { id: string; messages: unknown[] } | null
}

async function getOrCreateSessionToken(
  apiBaseUrl: string,
  customer?: { customerId?: string; customerEmail?: string; customerName?: string },
): Promise<SessionHandshake> {
  // Pass the logged-in Shopify identity so the backend can stamp
  // shopify_customer_id on the session — the strongest cross-device
  // attribution key (matches the chatter to ALL their orders regardless of
  // checkout email). Sent on BOTH resume and mint so a returning logged-in
  // customer gets backfilled. Empty for guests.
  const cp = new URLSearchParams()
  if (customer?.customerId) cp.set("cid", customer.customerId)
  if (customer?.customerEmail) cp.set("cemail", customer.customerEmail)
  if (customer?.customerName) cp.set("cname", customer.customerName)
  const cpStr = cp.toString()


  const stored =
    typeof localStorage !== "undefined"
      ? localStorage.getItem(SESSION_TOKEN_KEY)
      : null

  // Validate cached token before reusing — and grab any captured identity
  // off the session so the widget knows whether to show the identity gate.
  if (stored) {
    try {
      const r = await fetch(
        `${apiBaseUrl}/api/chat/session?token=${encodeURIComponent(stored)}${cpStr ? `&${cpStr}` : ""}`,
        { method: "GET" },
      )
      if (r.ok) {
        const body = (await r.json()) as {
          session?: { customer_email?: string | null; customer_name?: string | null }
          canvas_enabled?: boolean
          canvas_enabled_mobile?: boolean
          csat_enabled?: boolean
          conversation?: { id: string } | null
          messages?: unknown[]
        }
        return {
          token: stored,
          customerName: body.session?.customer_name ?? null,
          customerEmail: body.session?.customer_email ?? null,
          canvasEnabled:
            typeof body.canvas_enabled === "boolean"
              ? body.canvas_enabled
              : null,
          canvasEnabledMobile:
            typeof body.canvas_enabled_mobile === "boolean"
              ? body.canvas_enabled_mobile
              : null,
          // Default FALSE when absent — off means no behavior change.
          csatEnabled: body.csat_enabled === true,
          // Auto-resume: when the session has a recent conversation, ship
          // it back so the widget can hydrate without the customer having
          // to open the "Conversations" dropdown and pick. The widget
          // displays it on mount if non-empty.
          resumeConversation:
            body.conversation?.id && Array.isArray(body.messages) && body.messages.length > 0
              ? { id: body.conversation.id, messages: body.messages }
              : null,
        }
      }
      // 401/404/410: token stale. Drop it and fall through to fresh mint.
      try {
        localStorage.removeItem(SESSION_TOKEN_KEY)
      } catch {
        /* ignore */
      }
    } catch {
      // Network error — proceed to fresh mint.
    }
  }

  // Mint a fresh session. If the page has the staff-test flag set in
  // localStorage, pass ?test=1 so server-side `chat_sessions.is_test` gets
  // stamped true and the session is excluded from production analytics.
  let testFlag = ""
  try {
    if (localStorage.getItem("dyc-pearl-test") === "1") {
      testFlag = "test=1"
    }
  } catch {
    /* ignore */
  }
  // Combine cid/cemail/cname + test=1 into a single querystring. Either
  // can be empty; the join handles both gracefully.
  const queryParts = [cpStr, testFlag].filter(Boolean)
  const queryString = queryParts.length ? `?${queryParts.join("&")}` : ""
  try {
    const res = await fetch(
      `${apiBaseUrl}/api/chat/session${queryString}`,
      { method: "GET" },
    )
    if (res.ok) {
      const data = (await res.json()) as {
        token?: string
        session_token?: string
        session?: { customer_email?: string | null; customer_name?: string | null }
        canvas_enabled?: boolean
        canvas_enabled_mobile?: boolean
        csat_enabled?: boolean
      }
      const token = data.token ?? data.session_token
      if (token) {
        try {
          localStorage.setItem(SESSION_TOKEN_KEY, token)
        } catch {
          // localStorage blocked (incognito) — return token without caching.
        }
        // Returning-visitor recognition: createSession hydrates email/name
        // from prior matching-fingerprint sessions, so a brand-new token
        // can still ship with identity attached. Skip the gate if so.
        return {
          token,
          customerName: data.session?.customer_name ?? null,
          customerEmail: data.session?.customer_email ?? null,
          canvasEnabled:
            typeof data.canvas_enabled === "boolean"
              ? data.canvas_enabled
              : null,
          canvasEnabledMobile:
            typeof data.canvas_enabled_mobile === "boolean"
              ? data.canvas_enabled_mobile
              : null,
          // Default FALSE when absent — off means no behavior change.
          csatEnabled: data.csat_enabled === true,
          // Fresh mint has no prior conversation by definition.
          resumeConversation: null,
        }
      }
    }
  } catch (err) {
    console.warn("[pearl-widget] session mint failed:", err)
  }
  // Fallback: anon token. The backend will reject this on POST, but at
  // least the UI renders so we can surface the error.
  return {
    token: `anon-${crypto.randomUUID()}`,
    customerName: null,
    customerEmail: null,
    canvasEnabled: null,
    canvasEnabledMobile: null,
    csatEnabled: false,
    resumeConversation: null,
  }
}

/**
 * Plant Pearl's session token as a Shopify cart attribute so it rides
 * through checkout into `order.note_attributes` ─ closes the attribution
 * loop for anonymous chatters (who never gave us an email). The orders
 * sync extracts the attribute server-side and adds a session-id-based
 * match path alongside email-match.
 *
 * Why this works: Shopify cart attributes auto-propagate to the resulting
 * order's note_attributes array. No theme changes needed; the widget
 * runs on every storefront page and POSTs once per session.
 *
 * Idempotent: re-running with the same value is a no-op for Shopify.
 * Guarded so we don't spam the cart endpoint on every page load — we
 * keep a per-token-per-session flag in sessionStorage.
 */
async function plantPearlSessionInCart(token: string): Promise<void> {
  // Skip on dashboard / non-storefront contexts (no Shopify cart endpoint
  // there). Detection: presence of Shopify cart URLs.
  if (typeof window === "undefined") return
  if (
    !window.location.hostname.includes("dipyourcar") &&
    !window.location.hostname.includes("myshopify")
  ) {
    return
  }
  // Dedupe per token within the tab/session.
  const FLAG_KEY = "dyc-pearl-cart-plant"
  try {
    if (sessionStorage.getItem(FLAG_KEY) === token) return
  } catch {
    /* ignore */
  }
  try {
    const r = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: { dyc_pearl_session: token },
      }),
    })
    if (r.ok) {
      try {
        sessionStorage.setItem(FLAG_KEY, token)
      } catch {
        /* ignore */
      }
    }
  } catch {
    // Cart endpoint unreachable (network issue, CSP) — fail silent.
    // Attribution will fall back to email matching for this session.
  }
}

/** Fetch the customizer config (accent + dock_label) from the dashboard
 *  API. Best-effort — failures fall back to tokens.css defaults. */
async function fetchSettings(apiBaseUrl: string): Promise<Settings> {
  try {
    const r = await fetch(`${apiBaseUrl}/api/widget/config`)
    if (r.ok) return (await r.json()) as Settings
  } catch {
    // Network error or CORS issue — silent fallback.
  }
  return {}
}

function readPageContext(attrs: ReturnType<typeof readScriptAttrs>): PageContext {
  if (typeof window === "undefined") return {}
  const path = window.location.pathname
  const productMatch = path.match(/\/products\/([^/?#]+)/)
  return {
    currentPage: path,
    productHandle: productMatch?.[1],
    device: window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop",
    customerName: attrs.customerName,
    customerEmail: attrs.customerEmail,
    customerId: attrs.customerId,
  }
}

function hexToRgba(hex: string, alpha: number): string {
  const m = hex.replace("#", "")
  if (m.length !== 6) return `rgba(217,68,44,${alpha})`
  const r = parseInt(m.slice(0, 2), 16)
  const g = parseInt(m.slice(2, 4), 16)
  const b = parseInt(m.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

async function mount(): Promise<void> {
  if (typeof document === "undefined") return
  if (document.getElementById(ROOT_ID)) return

  const attrs = readScriptAttrs()
  const apiBaseUrl = attrs.apiBaseUrl ?? window.location.origin

  const [handshake, settings] = await Promise.all([
    getOrCreateSessionToken(apiBaseUrl, {
      customerId: attrs.customerId,
      customerEmail: attrs.customerEmail,
      customerName: attrs.customerName,
    }),
    fetchSettings(apiBaseUrl),
  ])
  const sessionToken = handshake.token

  // A/B experiment variants for this session (deterministic per session_id
  // on the server). Best-effort — failures fall back to control behaviour
  // in the widget. We only fetch active experiments.
  let experiments: Record<string, string> = {}
  if (sessionToken && !sessionToken.startsWith("anon-")) {
    try {
      const r = await fetch(
        `${apiBaseUrl}/api/chat/experiments?sessionToken=${encodeURIComponent(sessionToken)}`,
      )
      if (r.ok) {
        const body = (await r.json()) as { assignments?: Record<string, string> }
        experiments = body.assignments ?? {}
      }
    } catch {
      /* swallow — variants default to control */
    }
  }

  // Plant the Pearl session token as a Shopify cart attribute (best-effort,
  // fire-and-forget). Closes the attribution loop for anonymous chatters
  // by making the session_id flow through checkout to order.note_attributes.
  if (sessionToken && !sessionToken.startsWith("anon-")) {
    void plantPearlSessionInCart(sessionToken)
  }

  const initialContext = readPageContext(attrs)
  // Returning-visitor identity: hydrate initialContext with any name/email
  // already on the session (from a prior chat, fingerprint-matched on
  // server-side createSession). The widget treats `customerName` /
  // `customerEmail` from initialContext as "already captured" → no gate.
  // The script-tag dataset (Shopify logged-in customer) still takes
  // precedence — those are the highest-confidence source.
  if (!initialContext.customerName && handshake.customerName) {
    initialContext.customerName = handshake.customerName
  }
  if (!initialContext.customerEmail && handshake.customerEmail) {
    initialContext.customerEmail = handshake.customerEmail
  }

  // ── Pearl mini-cart canvas feature flag (per-platform) ───────────────
  // The canvas is toggled independently for desktop (≥768px) and mobile
  // (≤767px) in /chat/settings. We resolve WHICH flag applies from the
  // viewport at mount, then layer the QA/ops overrides on top.
  //
  // Resolution order (highest priority first):
  //   1. URL `?canvas=0|1` — explicit override for QA + ops smoke-tests
  //   2. localStorage `dyc-pearl-canvas` — legacy QA force-on
  //   3. Server per-platform flag from the /api/chat/session handshake
  //      (desktop default TRUE, mobile default FALSE)
  //   4. Final fallback: desktop TRUE / mobile FALSE — don't regress desktop
  //      on a first-mount hiccup; mobile canvas is opt-in.
  //
  // NOTE: resolved at mount, not reactively — a real customer's device never
  // crosses the 767px line mid-session (a phone is always mobile, a desktop
  // always desktop). Only a dev resizing the window would; harmless.
  const isMobileViewport =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 767px)").matches

  const desktopCanvas = handshake.canvasEnabled !== null ? handshake.canvasEnabled : true
  const mobileCanvas =
    handshake.canvasEnabledMobile !== null ? handshake.canvasEnabledMobile : false

  let canvasEnabled: boolean = isMobileViewport ? mobileCanvas : desktopCanvas
  try {
    if (localStorage.getItem("dyc-pearl-canvas") === "1") canvasEnabled = true
  } catch {
    /* incognito — ignore */
  }
  if (typeof window !== "undefined") {
    const p = new URLSearchParams(window.location.search).get("canvas")
    if (p === "1" || p === "true") canvasEnabled = true
    else if (p === "0" || p === "false") canvasEnabled = false
  }

  const root = document.createElement("div")
  root.id = ROOT_ID
  // Inline-set the CSS vars for accent overrides — these win over
  // tokens.css since they're inline (highest specificity).
  if (settings.accent) {
    root.style.setProperty("--vw-accent", settings.accent)
    root.style.setProperty("--vw-accent-soft", hexToRgba(settings.accent, 0.12))
  }
  document.body.appendChild(root)

  const opts: WidgetMountOptions = { apiBaseUrl }
  const dockLabel =
    attrs.dockLabelOverride ?? settings.dock_label ?? "Ask Pearl anything"

  // Fin-style "Messenger" is now the default UI. The original dark-glass
  // widget stays reachable via data-variant="classic" (or ?pearl_variant=classic)
  // for A/B comparison.
  const Widget = attrs.variant === "classic" ? ChatWidget : FinChatWidget

  createRoot(root).render(
    <Widget
      mountOptions={opts}
      sessionToken={sessionToken}
      initialContext={initialContext}
      dockLabel={dockLabel}
      canvasEnabled={canvasEnabled}
      csatEnabled={handshake.csatEnabled}
      experiments={experiments}
      resumeConversation={handshake.resumeConversation}
    />,
  )
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      void mount()
    })
  } else {
    void mount()
  }
}
